/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  Alert,
  Box,
  Button,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React, { ChangeEvent, useEffect, useState } from "react";
import { UserRole } from "../../../@types/DRO/UserManagementResponse";
import { GetAllVendorsListApi } from "../../../api/GetVendorsList/GetAllVendorsListApi";
import { UserManagementApi } from "../../../api/UserManagement/UserManagementApi";
import { useLoading } from "../../../views/Loader/LoadingContext";

interface CreateDeliveryOrderProps {
  onBack: () => void;
}

const CreateVendor: React.FC<CreateDeliveryOrderProps> = ({ onBack }) => {
  const { setLoading } = useLoading();
  const [roles, setRoles] = useState<UserRole[]>([]);
  const [selectedRoleId, setSelectedRoleId] = useState<number | "">("");
  const [vendorForm, setVendorForm] = useState({
    name: "",
    address: {
      name: "",
      addressType: "",
      addressLineOne: "",
      addressLineTwo: "",
      city: "",
      state: "",
      country: "",
      pinCode: "",
      latitude: 0, // keep for type compatibility
      longitude: 0, // keep for type compatibility
    },
    contact: "",
    email: "",
    gst: "",
    hsnCode: "",
    vat: "",
    panNumber: "",
    vendorType: "",
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [errors, setErrors] = useState<any>({});

  useEffect(() => {
    UserManagementApi.api.getRoles()
      .then(setRoles)
      .catch(() => {});
  }, []);

  const handleVendorInput = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string,
    nested?: boolean
  ) => {
    if (nested) {
      setVendorForm({
        ...vendorForm,
        address: {
          ...vendorForm.address,
          [field]: e.target.value,
        },
      });
    } else {
      setVendorForm({ ...vendorForm, [field]: e.target.value });
    }
  };

  const handleVendorNumberInput = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    field: string
  ) => {
    setVendorForm({
      ...vendorForm,
      address: {
        ...vendorForm.address,
        [field]: Number(e.target.value),
      },
    });
  };

  const validate = () => {
    const newErrors: any = {};
    // Required fields
    if (!vendorForm.name.trim()) newErrors.name = "Vendor name is required";
    if (!vendorForm.contact.trim()) newErrors.contact = "Contact is required";
    if (!vendorForm.email.trim()) newErrors.email = "Email is required";
    if (!vendorForm.address.city.trim()) newErrors.city = "City is required";
    if (!vendorForm.address.state.trim()) newErrors.state = "State is required";
    if (!vendorForm.address.country.trim())
      newErrors.country = "Country is required";
    if (!vendorForm.address.pinCode.trim())
      newErrors.pinCode = "Pin code is required";
    if (!vendorForm.address.addressType.trim())
      newErrors.addressType = "Address type is required";
    // Email format
    if (
      vendorForm.email &&
      !/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/.test(vendorForm.email)
    ) {
      newErrors.email = "Invalid email format";
    }
    // Contact: numeric, max 10 digits
    if (vendorForm.contact && !/^[0-9]{1,10}$/.test(vendorForm.contact)) {
      newErrors.contact = "Contact must be numeric and up to 10 digits";
    }
    // City, state, country: alphabetic only
    if (
      vendorForm.address.city &&
      !/^[A-Za-z ]+$/.test(vendorForm.address.city)
    ) {
      newErrors.city = "City must contain only letters";
    }
    if (
      vendorForm.address.state &&
      !/^[A-Za-z ]+$/.test(vendorForm.address.state)
    ) {
      newErrors.state = "State must contain only letters";
    }
    if (
      vendorForm.address.country &&
      !/^[A-Za-z ]+$/.test(vendorForm.address.country)
    ) {
      newErrors.country = "Country must contain only letters";
    }
    // Pin code: numeric only
    if (
      vendorForm.address.pinCode &&
      !/^[0-9]+$/.test(vendorForm.address.pinCode)
    ) {
      newErrors.pinCode = "Pin code must be numeric";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateVendor = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const { vendorType: _vendorType, ...formWithoutVendorType } = vendorForm;
      const payload: any = { ...formWithoutVendorType };
      if (selectedRoleId !== "") {
        payload.roleId = Number(selectedRoleId);
      }
      await GetAllVendorsListApi.api.createVendor(payload);
      setSnackbar({
        open: true,
        message: "Vendor created successfully",
        severity: "success",
      });
      setTimeout(() => {
        onBack();
      }, 1500);
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error creating vendor",
        severity: "error",
      });
    }
    setLoading(false);
  };

  const handleCloseSnackbar = () => setSnackbar({ ...snackbar, open: false });

  return (
    <Box sx={{ padding: 4 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ textAlign: "left", fontWeight: "600", color: "#1E1E1E", mb: 2 }}>
        Create Vendor
      </Typography>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 4,
          backgroundColor: "#F8F9FA",
          padding: "12px 16px",
          borderRadius: "8px",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
        }}>
        <Button
          onClick={onBack}
          sx={{
            textTransform: "none",
            color: "#646464",
            p: 0,
            "&:hover": { color: "#1E1E1E", backgroundColor: "transparent" },
          }}>
          Dashboard
        </Button>
        <Typography variant="body2" color="#646464" sx={{ mx: 1 }}>
          &gt;
        </Typography>
        <Button
          onClick={onBack}
          sx={{
            textTransform: "none",
            color: "#646464",
            p: 0,
            "&:hover": { color: "#1E1E1E", backgroundColor: "transparent" },
          }}>
          Vendor
        </Button>
        <Typography variant="body2" color="#646464" sx={{ mx: 1 }}>
          &gt;
        </Typography>
        <Typography variant="body2" color="#1E1E1E" sx={{ fontWeight: "500" }}>
          Create Vendor
        </Typography>
      </Box>
      <Paper
        sx={{
          padding: 3,
          marginTop: 2,
          borderRadius: "8px",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        }}>
        <Grid container spacing={3} sx={{ textAlign: "left" }}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Vendor Name"
              fullWidth
              variant="standard"
              value={vendorForm.name}
              onChange={(e) => handleVendorInput(e, "name")}
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Contact"
              fullWidth
              variant="standard"
              value={vendorForm.contact}
              onChange={(e) => {
                if (/^\d{0,10}$/.test(e.target.value))
                  handleVendorInput(e, "contact");
              }}
              error={!!errors.contact}
              helperText={errors.contact}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Email"
              fullWidth
              variant="standard"
              value={vendorForm.email}
              onChange={(e) => handleVendorInput(e, "email")}
              error={!!errors.email}
              helperText={errors.email}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="GST"
              fullWidth
              variant="standard"
              value={vendorForm.gst}
              onChange={(e) => handleVendorInput(e, "gst")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="HSN Code"
              fullWidth
              variant="standard"
              value={vendorForm.hsnCode}
              onChange={(e) => handleVendorInput(e, "hsnCode")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="VAT"
              fullWidth
              variant="standard"
              value={vendorForm.vat}
              onChange={(e) => handleVendorInput(e, "vat")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="PAN Number"
              fullWidth
              variant="standard"
              value={vendorForm.panNumber}
              onChange={(e) => handleVendorInput(e, "panNumber")}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Vendor Type (Optional)"
              fullWidth
              variant="standard"
              value={selectedRoleId}
              onChange={(e) => {
                const id = Number(e.target.value);
                setSelectedRoleId(id);
                setVendorForm((prev) => ({ ...prev, vendorType: String(id) }));
              }}>
              {roles
                .filter((r) =>
                  ["ROLE_ERP_REFURB_VENDOR", "ROLE_ERP_WARRANTY_VENDOR"].includes(r.role)
                )
                .map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.role}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          {/* Address Fields */}
          <Grid item xs={12}>
            <h3 style={{ fontWeight: 500, marginBottom: 8 }}>Address</h3>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Address Name"
              fullWidth
              variant="standard"
              value={vendorForm.address.name}
              onChange={(e) => handleVendorInput(e, "name", true)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              label="Address Type"
              fullWidth
              variant="standard"
              value={vendorForm.address.addressType}
              onChange={(e) => handleVendorInput(e, "addressType", true)}
              error={!!errors.addressType}
              helperText={errors.addressType}>
              <MenuItem value="EXTERNAL">External</MenuItem>
              <MenuItem value="INTERNAL">Internal</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Address Line 1"
              fullWidth
              variant="standard"
              value={vendorForm.address.addressLineOne}
              onChange={(e) => handleVendorInput(e, "addressLineOne", true)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Address Line 2"
              fullWidth
              variant="standard"
              value={vendorForm.address.addressLineTwo}
              onChange={(e) => handleVendorInput(e, "addressLineTwo", true)}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="City"
              fullWidth
              variant="standard"
              value={vendorForm.address.city}
              onChange={(e) => {
                if (/^[A-Za-z ]*$/.test(e.target.value))
                  handleVendorInput(e, "city", true);
              }}
              error={!!errors.city}
              helperText={errors.city}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="State"
              fullWidth
              variant="standard"
              value={vendorForm.address.state}
              onChange={(e) => {
                if (/^[A-Za-z ]*$/.test(e.target.value))
                  handleVendorInput(e, "state", true);
              }}
              error={!!errors.state}
              helperText={errors.state}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Country"
              fullWidth
              variant="standard"
              value={vendorForm.address.country}
              onChange={(e) => {
                if (/^[A-Za-z ]*$/.test(e.target.value))
                  handleVendorInput(e, "country", true);
              }}
              error={!!errors.country}
              helperText={errors.country}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Pin Code"
              fullWidth
              variant="standard"
              value={vendorForm.address.pinCode}
              onChange={(e) => {
                if (/^\d*$/.test(e.target.value))
                  handleVendorInput(e, "pinCode", true);
              }}
              error={!!errors.pinCode}
              helperText={errors.pinCode}
            />
          </Grid>
        </Grid>
        <Box sx={{ marginTop: 4, textAlign: "right" }}>
          <Button
            variant="contained"
            onClick={handleCreateVendor}
            color="primary"
            sx={{
              padding: "10px 20px",
              borderRadius: "32px",
              backgroundColor: "#1E1E1E",
              textTransform: "none",
              fontWeight: "500",
              "&:hover": { backgroundColor: "#000000" },
            }}>
            Create Vendor
          </Button>
        </Box>
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={2000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CreateVendor;
